/**
 * HypeFly · Gutschein-Popup (Promo-Modal auf `/aktion`)
 * ======================================================
 *
 * Trigger-Logik:
 *  - `trigger_seconds` (> 0): öffnet nach so vielen Sekunden auf der
 *    Seite. 0 = deaktiviert.
 *  - `trigger_scroll_percent` (> 0): öffnet, sobald so viel % der
 *    Seite gescrollt wurden. 0 = deaktiviert.
 *  - Was zuerst zutrifft, öffnet.
 *
 * Dismissal:
 *  - SessionStorage-Key `hf_gutschein_dismissed` = "1" → kein
 *    erneutes Anzeigen in dieser Session (Tab).
 *  - Wird beim Schließen ODER erfolgreichem Submit gesetzt.
 *
 * Countdown:
 *  - `midnight` → tickt zu heute 23:59:59 Ortszeit (rolling daily).
 *  - `fixed`    → tickt zu `countdown_target_iso`.
 *  - `off`      → kein Countdown-Block gerendert.
 *
 * Formular:
 *  - Nur bei `form_enabled = true`: Name/Tel/PLZ + Datenschutz.
 *  - Submit an `/api/contact` mit `source="gutschein"` +
 *    `campaign=<campaign_tag>` + `discount_percent=<n>`.
 *  - Erfolgs-State ersetzt das Formular in-place.
 *  - Bei `form_enabled = false`: CTA scrollt zu `#aktion-kontakt`.
 *
 * Design:
 *  - Dark navy Modal (#0F1830) mit Gold-Border (#D4A855).
 *  - Roter Zackenkreis mit Prozent-Zahl mittig.
 *  - Gold-CTA mit Chevron-Right.
 *  - Trust-Badges als 2 Gold-Boxen nebeneinander.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronRight, ShieldCheck, Lock, Check } from "lucide-react";
import { currentVtCode, trackConversion, trackAktion } from "@/lib/analytics";
import type { GutscheinPopupConfig, ReferenzPopupConfig } from "@/lib/useAktionsSeite";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;
const DISMISS_KEY = "hf_gutschein_dismissed";
const PLZ_RE = /^\d{5}$/;

type Status = "idle" | "sending" | "success" | "error";

/** Formatiert eine Rest-Millisekunden-Anzahl als "HH : MM : SS". */
function formatCountdown(remainingMs: number): { h: string; m: string; s: string } {
    if (remainingMs <= 0) return { h: "00", m: "00", s: "00" };
    const totalSec = Math.floor(remainingMs / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const pad = (n: number) => n.toString().padStart(2, "0");
    return { h: pad(h), m: pad(m), s: pad(s) };
}

/** Berechnet das nächste Countdown-Ziel (ms since epoch) oder null wenn "off". */
function computeTarget(cfg: GutscheinPopupConfig): number | null {
    if (cfg.countdown_mode === "off") return null;
    if (cfg.countdown_mode === "fixed") {
        if (!cfg.countdown_target_iso) return null;
        const t = new Date(cfg.countdown_target_iso).getTime();
        return Number.isFinite(t) ? t : null;
    }
    // midnight (default)
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    return end.getTime();
}

/** Rendert den Privacy-Text mit `{link}`-Platzhalter → klickbarer Link. */
function PrivacyText({ text, linkText, linkUrl }: { text: string; linkText: string; linkUrl: string }) {
    const parts = (text || "").split("{link}");
    if (parts.length < 2) return <>{text}</>;
    return (
        <>
            {parts[0]}
            <a
                href={linkUrl || "/datenschutz"}
                target="_blank"
                rel="noreferrer"
                className="underline"
                style={{ color: "#D4A855" }}
            >
                {linkText || "Datenschutzerklärung"}
            </a>
            {parts.slice(1).join("{link}")}
        </>
    );
}

/** Zwei visuelle/inhaltliche Modi. „rabatt" = bestehendes Verhalten
 *  (80 % Rabatt, rot/gold, Stern-Badge), „referenz" = 100 % Kostenlos-Modus
 *  mit komplett eigenem Layout (Gold-Zertifikats-Siegel, ohne Countdown,
 *  ohne „€"-Wortlaut). Wird als Prop übergeben und schaltet Textquelle,
 *  Layout und Backend-Tag um. */
export type GutscheinMode = "referenz" | "rabatt";

type Props = {
    cfg: GutscheinPopupConfig;
    /** Ziel-Element-ID für Fallback-Scroll (wenn `form_enabled = false`). */
    fallbackScrollTargetId?: string;
    /** Modus des Popups. Default `"rabatt"`. */
    mode?: GutscheinMode;
    /** Texte / Konfiguration für das Referenz-Layout (mode="referenz").
     *  Wird nur ausgewertet, wenn `mode === "referenz"`. */
    referenzCfg?: ReferenzPopupConfig;
    /**
     * Externer Öffnungs-Trigger. Jedes Increment (Nonce) öffnet das Popup
     * sofort — unabhängig von Trigger-Config und SessionStorage-Sperre.
     * Wird vom Choice-Popup-Flow verwendet, wenn der User "100 %" oder "80 %"
     * gewählt hat. Der Default-Wert `0` bedeutet "nie manuell geöffnet".
     */
    openSignal?: number;
    /**
     * Suspendiert den internen Auto-Trigger (Zeit + Scroll) solange `true`.
     * `openSignal` bleibt weiter aktiv (explizite User-Wahl). Wird von der
     * `AktionHome`-Seite auf `true` gesetzt, solange das Welcome-Choice-
     * Popup sichtbar ist — verhindert dass sich der Rabatt-Auto-Trigger
     * hinter dem Welcome-Modal aufbaut. Sobald das Welcome dismissed wird,
     * kippt das Flag auf `false` und der reguläre Second-Chance-Trigger
     * greift wieder normal.
     */
    suspendAutoTrigger?: boolean;
};

export default function GutscheinPopup({
    cfg, fallbackScrollTargetId = "aktion-kontakt",
    mode = "rabatt", referenzCfg, openSignal = 0,
    suspendAutoTrigger = false,
}: Props) {
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState<Status>("idle");
    const [errorMsg, setErrorMsg] = useState<string>("");
    const [datenschutz, setDatenschutz] = useState(false);
    const [errs, setErrs] = useState<{ name?: string; plz?: string; phone?: string; datenschutz?: string }>({});
    const [remainingMs, setRemainingMs] = useState<number>(0);
    const alreadyOpenedRef = useRef(false);

    // Session-Dismissal: einmal geschlossen → nicht mehr in dieser Session.
    const alreadyDismissed = useCallback((): boolean => {
        try {
            return typeof window !== "undefined" && window.sessionStorage?.getItem(DISMISS_KEY) === "1";
        } catch {
            return false;
        }
    }, []);
    const markDismissed = useCallback(() => {
        try {
            window.sessionStorage?.setItem(DISMISS_KEY, "1");
        } catch { /* Storage kann in Privacy-Mode blockiert sein */ }
    }, []);

    // Mode-abhängige Werte: bei `referenz` wird der Star zu "0 €", der
    // Campaign-Tag zu `referenz-gutschein` und `discount_percent=100` an
    // das Backend gesendet. Farben werden weiter unten (siehe `accent…`
    // Konstanten) mode-abhängig gesetzt.
    const modeCampaign = mode === "referenz" ? "referenz-gutschein" : (cfg.campaign_tag || "gutschein");
    const modeDiscountPct = mode === "referenz" ? 100 : (cfg.discount_percent || 0);
    // Externes Öffnen via `openSignal` (Nonce). Jedes Increment öffnet das
    // Popup, auch wenn der SessionStorage-Dismissal-Flag gesetzt ist —
    // Nutzer hat aktiv im Choice-Popup gewählt.
    const lastSignalRef = useRef(0);
    useEffect(() => {
        if (openSignal && openSignal !== lastSignalRef.current) {
            lastSignalRef.current = openSignal;
            alreadyOpenedRef.current = true;
            setOpen(true);
            setStatus("idle");
            try { trackAktion("gutschein_popup_opened", { source: "welcome_choice", mode }); } catch { /* nicht kritisch */ }
        }
    }, [openSignal, mode]);

    // Trigger-Logik: Timer + Scroll-Listener parallel. Suspendiert, solange
    // das Welcome-Choice-Popup sichtbar ist (`suspendAutoTrigger`), damit
    // sich das Rabatt-Popup nicht heimlich hinter dem Welcome-Modal öffnet.
    // Wenn `suspendAutoTrigger` von `true` auf `false` kippt (Welcome
    // dismissed), re-runt der Effect und die Trigger greifen ab dann normal.
    useEffect(() => {
        if (!cfg.enabled) return;
        if (suspendAutoTrigger) return;
        if (alreadyDismissed()) return;

        const openOnce = () => {
            if (alreadyOpenedRef.current) return;
            alreadyOpenedRef.current = true;
            setOpen(true);
            try { trackAktion("gutschein_popup_opened"); } catch { /* nicht kritisch */ }
        };

        // Zeit-Trigger
        let timerId: ReturnType<typeof setTimeout> | null = null;
        if (cfg.trigger_seconds > 0) {
            timerId = setTimeout(openOnce, cfg.trigger_seconds * 1000);
        }

        // Scroll-Trigger
        let scrollHandler: (() => void) | null = null;
        if (cfg.trigger_scroll_percent > 0) {
            scrollHandler = () => {
                if (alreadyOpenedRef.current) return;
                const doc = document.documentElement;
                const scrollable = Math.max(1, doc.scrollHeight - doc.clientHeight);
                const pct = (window.scrollY / scrollable) * 100;
                if (pct >= cfg.trigger_scroll_percent) {
                    openOnce();
                }
            };
            window.addEventListener("scroll", scrollHandler, { passive: true });
        }

        return () => {
            if (timerId) clearTimeout(timerId);
            if (scrollHandler) window.removeEventListener("scroll", scrollHandler);
        };
    }, [cfg.enabled, cfg.trigger_seconds, cfg.trigger_scroll_percent, alreadyDismissed, suspendAutoTrigger]);

    // Countdown-Ticker (nur wenn Popup offen und Modus != off).
    useEffect(() => {
        if (!open || cfg.countdown_mode === "off") return;
        const tick = () => {
            const target = computeTarget(cfg);
            if (target === null) {
                setRemainingMs(0);
                return;
            }
            setRemainingMs(Math.max(0, target - Date.now()));
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [open, cfg]);

    // Body-Scroll-Lock während Popup offen.
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    // ESC-Taste zum Schließen. Muss NACH `handleClose` initialisiert werden,
    // damit die TS block-scope-Regel nicht klagt.
    // (Der Effekt selbst wird ohnehin erst nach dem Render evaluiert.)

    const handleClose = useCallback(() => {
        setOpen(false);
        markDismissed();
        try { trackAktion("gutschein_popup_dismissed"); } catch { /* nicht kritisch */ }
    }, [markDismissed]);

    const scrollToFallbackAndClose = useCallback(() => {
        setOpen(false);
        markDismissed();
        const el = document.getElementById(fallbackScrollTargetId);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [fallbackScrollTargetId, markDismissed]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") handleClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, handleClose]);

    const cd = useMemo(() => formatCountdown(remainingMs), [remainingMs]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setErrorMsg("");
        const form = e.currentTarget;
        const name = (form.elements.namedItem("gname") as HTMLInputElement).value.trim();
        const phone = (form.elements.namedItem("gphone") as HTMLInputElement).value.trim();
        const plz = (form.elements.namedItem("gplz") as HTMLInputElement).value.trim();
        const nextErrs: typeof errs = {};
        if (name.length < 2) nextErrs.name = "Bitte geben Sie Ihren Namen an.";
        if (phone.length < 5) nextErrs.phone = "Bitte geben Sie eine Telefonnummer an.";
        if (!PLZ_RE.test(plz)) nextErrs.plz = "Bitte eine 5-stellige PLZ.";
        if (!datenschutz) nextErrs.datenschutz = "Bitte bestätigen Sie den Datenschutzhinweis.";
        if (Object.keys(nextErrs).length > 0) {
            setErrs(nextErrs);
            return;
        }
        setErrs({});
        setStatus("sending");

        const fd = new FormData();
        fd.append("name", name);
        fd.append("email", `${plz}-gutschein@no-email.hypefly.de`);
        fd.append("phone", phone);
        fd.append("location", plz);
        fd.append(
            "message",
            mode === "referenz"
                ? `Referenz-Reservierung (100 % kostenlos · Kampagne ${modeCampaign}). ` +
                  `Bitte unverbindlich telefonisch zurückrufen. PLZ: ${plz}, Tel: ${phone}.`
                : `Gutschein-Reservierung (${cfg.discount_percent}% Rabatt · Kampagne ${modeCampaign}). ` +
                  `Bitte unverbindlich telefonisch zurückrufen. PLZ: ${plz}, Tel: ${phone}.`,
        );
        fd.append("datenschutz", "true");
        fd.append("source", "gutschein");
        fd.append("campaign", modeCampaign);
        fd.append("discount_percent", String(modeDiscountPct));
        fd.append("honeypot", (form.elements.namedItem("website") as HTMLInputElement).value);
        const vt = currentVtCode();
        if (vt) fd.append("vt_code", vt);

        try {
            const res = await fetch(`${BACKEND_URL}/api/contact`, { method: "POST", body: fd });
            let data: unknown = null;
            try { data = await res.json(); } catch { /* ignore */ }
            if (!res.ok) {
                setStatus("error");
                const detail = (data as { detail?: string })?.detail;
                setErrorMsg(typeof detail === "string" ? detail : `Fehler (HTTP ${res.status}).`);
                return;
            }
            setStatus("success");
            markDismissed();
            try {
                trackConversion({ source: "gutschein", plz });
                trackAktion("gutschein_form_submitted");
            } catch { /* nicht kritisch */ }
        } catch (err) {
            setStatus("error");
            setErrorMsg((err as Error).message || "Verbindung fehlgeschlagen.");
        }
    };

    if (!open) return null;

    const showCountdown = cfg.countdown_mode !== "off";
    const soldOut = showCountdown && remainingMs <= 0;
    const inputCls =
        "w-full px-3 py-2.5 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-[#D4A855] transition";
    const inputStyle: React.CSSProperties = {
        background: "#0B1220",
        color: "#F8FAFC",
        border: "1.5px solid #33415C",
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Gutschein-Aktion"
            data-testid="gutschein-popup"
            className="fixed inset-0 z-[1000] flex items-center justify-center px-4 py-6 md:py-10"
            style={{
                background: "rgba(4, 8, 20, 0.78)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
            }}
            onClick={(e) => {
                // Klick auf Backdrop → schließen. Klick auf Modal-Inhalt: nicht.
                if (e.target === e.currentTarget) handleClose();
            }}
        >
            <div
                className="relative w-full max-w-lg rounded-lg overflow-hidden max-h-[92vh] overflow-y-auto"
                style={{
                    background: "linear-gradient(180deg, #0F1830 0%, #0B1220 100%)",
                    border: "2px solid #D4A855",
                    boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,168,85,0.25)",
                    color: "#F8FAFC",
                }}
                data-testid="gutschein-popup-modal"
            >
                {/* Close-Button */}
                <button
                    type="button"
                    onClick={handleClose}
                    aria-label="Schließen"
                    data-testid="gutschein-popup-close"
                    className="absolute top-3 right-3 z-10 rounded-full p-1.5 hover:bg-white/10 transition"
                    style={{ color: "#F8FAFC" }}
                >
                    <X size={22} strokeWidth={2.4} />
                </button>

                {status === "success" ? (
                    <SuccessState
                        headline={mode === "referenz" && referenzCfg
                            ? referenzCfg.form_success_headline
                            : cfg.form_success_headline}
                        body={mode === "referenz" && referenzCfg
                            ? referenzCfg.form_success_body
                            : cfg.form_success_body}
                        onClose={handleClose}
                    />
                ) : mode === "referenz" && referenzCfg ? (
                    <ReferenzBody
                        rcfg={referenzCfg}
                        errs={errs}
                        errorMsg={errorMsg}
                        status={status}
                        datenschutz={datenschutz}
                        setDatenschutz={setDatenschutz}
                        onSubmit={handleSubmit}
                    />
                ) : (
                    <div className="px-6 md:px-8 py-8 md:py-10 flex flex-col items-center text-center gap-4">
                        {/* Kicker */}
                        {cfg.kicker ? (
                            <div
                                className="text-[10px] md:text-xs uppercase tracking-[0.32em]"
                                style={{ color: "#D4A855", fontWeight: 600 }}
                                data-testid="gutschein-popup-kicker"
                            >
                                {cfg.kicker}
                            </div>
                        ) : null}

                        {/* Headline */}
                        <h2
                            className="font-anton uppercase leading-[1.05]"
                            style={{ color: "#F8FAFC", fontSize: "1.9rem", letterSpacing: "0.005em" }}
                            data-testid="gutschein-popup-headline"
                        >
                            {cfg.headline}
                        </h2>

                        {/* Zackenkreis mit Prozentzahl (bzw. „0 €" im Referenz-Modus) */}
                        <DiscountStar
                            percent={cfg.discount_percent}
                            label={cfg.discount_label}
                            mode={mode}
                        />

                        {/* Body */}
                        {cfg.body ? (
                            <p
                                className="font-display text-xl md:text-2xl leading-snug -mt-1"
                                style={{ color: "#F8FAFC", fontStyle: "italic" }}
                                data-testid="gutschein-popup-body"
                            >
                                {cfg.body}
                            </p>
                        ) : null}

                        {/* Fine Print */}
                        {cfg.fine_print ? (
                            <p
                                className="text-xs md:text-sm"
                                style={{ color: "#D4A855", opacity: 0.9 }}
                                data-testid="gutschein-popup-fineprint"
                            >
                                {cfg.fine_print}
                            </p>
                        ) : null}

                        {/* Countdown */}
                        {showCountdown ? (
                            <div
                                className="flex items-baseline gap-2 mt-1"
                                data-testid="gutschein-popup-countdown"
                            >
                                {cfg.countdown_prefix ? (
                                    <span
                                        className="font-display italic"
                                        style={{ color: "#D4A855", fontSize: "1.1rem" }}
                                    >
                                        {cfg.countdown_prefix}
                                    </span>
                                ) : null}
                                {soldOut ? (
                                    <span
                                        className="font-anton uppercase"
                                        style={{ color: "#F8FAFC", fontSize: "1.5rem", letterSpacing: "0.05em" }}
                                    >
                                        Aktion abgelaufen
                                    </span>
                                ) : (
                                    <>
                                        <span
                                            className="tabular-nums"
                                            style={{
                                                color: "#F8FAFC",
                                                fontFamily: "'Anton', 'Impact', sans-serif",
                                                fontSize: "2rem",
                                                letterSpacing: "0.05em",
                                                fontWeight: 400,
                                            }}
                                        >
                                            {cd.h} : {cd.m} : {cd.s}
                                        </span>
                                        {cfg.countdown_suffix ? (
                                            <span
                                                className="font-display italic"
                                                style={{ color: "#D4A855", fontSize: "1rem" }}
                                            >
                                                {cfg.countdown_suffix}
                                            </span>
                                        ) : null}
                                    </>
                                )}
                            </div>
                        ) : null}

                        {/* Trust-Badges (2 Gold-Boxen) */}
                        {cfg.trust_badges && cfg.trust_badges.length > 0 ? (
                            <div
                                className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full mt-2"
                                data-testid="gutschein-popup-trust"
                            >
                                {cfg.trust_badges.slice(0, 4).map((label, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center gap-2 px-2.5 py-2 rounded-md text-[10.5px] sm:text-[11px] uppercase"
                                        style={{
                                            background: "rgba(212, 168, 85, 0.14)",
                                            border: "1px solid #D4A855",
                                            color: "#F8FAFC",
                                            fontWeight: 600,
                                            letterSpacing: "0.03em",
                                        }}
                                        data-testid={`gutschein-popup-trust-${i}`}
                                    >
                                        {i === 0 ? (
                                            <ShieldCheck size={16} strokeWidth={2.2} style={{ color: "#D4A855", flexShrink: 0 }} />
                                        ) : (
                                            <Lock size={16} strokeWidth={2.2} style={{ color: "#D4A855", flexShrink: 0 }} />
                                        )}
                                        <span className="leading-tight text-left">{label}</span>
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        {/* Mini-Formular ODER CTA-Button (Fallback) */}
                        {cfg.form_enabled ? (
                            <form
                                onSubmit={handleSubmit}
                                className="w-full mt-3 grid gap-3"
                                data-testid="gutschein-popup-form"
                                noValidate
                            >
                                {/* Honeypot */}
                                <label
                                    aria-hidden="true"
                                    style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}
                                >
                                    Website
                                    <input type="text" name="website" tabIndex={-1} autoComplete="off" />
                                </label>

                                {cfg.form_headline ? (
                                    <p
                                        className="font-anton uppercase text-lg tracking-wider text-center"
                                        style={{ color: "#F8FAFC" }}
                                        data-testid="gutschein-popup-form-headline"
                                    >
                                        {cfg.form_headline}
                                    </p>
                                ) : null}

                                <div className="text-left">
                                    <label className="block text-xs mb-1 font-medium" style={{ color: "#D4A855" }}>
                                        {cfg.form_name_label || "Name"} *
                                    </label>
                                    <input
                                        name="gname" type="text" required autoComplete="name"
                                        placeholder={cfg.form_name_placeholder || undefined}
                                        className={inputCls}
                                        style={{ ...inputStyle, borderColor: errs.name ? "#EF4444" : "#33415C" }}
                                        data-testid="gutschein-popup-name"
                                    />
                                    {errs.name && <p className="mt-1 text-xs" style={{ color: "#FCA5A5" }}>{errs.name}</p>}
                                </div>

                                <div className="grid grid-cols-2 gap-3 text-left">
                                    <div>
                                        <label className="block text-xs mb-1 font-medium" style={{ color: "#D4A855" }}>
                                            {cfg.form_phone_label || "Telefon"} *
                                        </label>
                                        <input
                                            name="gphone" type="tel" required autoComplete="tel"
                                            placeholder={cfg.form_phone_placeholder || undefined}
                                            className={inputCls}
                                            style={{ ...inputStyle, borderColor: errs.phone ? "#EF4444" : "#33415C" }}
                                            data-testid="gutschein-popup-phone"
                                        />
                                        {errs.phone && <p className="mt-1 text-xs" style={{ color: "#FCA5A5" }}>{errs.phone}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-xs mb-1 font-medium" style={{ color: "#D4A855" }}>
                                            {cfg.form_plz_label || "PLZ"} *
                                        </label>
                                        <input
                                            name="gplz" type="text" inputMode="numeric"
                                            pattern="[0-9]{5}" maxLength={5} required
                                            autoComplete="postal-code"
                                            placeholder={cfg.form_plz_placeholder || undefined}
                                            className={inputCls}
                                            style={{ ...inputStyle, borderColor: errs.plz ? "#EF4444" : "#33415C" }}
                                            data-testid="gutschein-popup-plz"
                                        />
                                        {errs.plz && <p className="mt-1 text-xs" style={{ color: "#FCA5A5" }}>{errs.plz}</p>}
                                    </div>
                                </div>

                                <label
                                    className="flex items-start gap-2 text-xs md:text-[13px] text-left"
                                    style={{ color: "#CBD5E1" }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={datenschutz}
                                        onChange={(e) => setDatenschutz(e.target.checked)}
                                        required
                                        className="mt-0.5 h-4 w-4"
                                        style={{ accentColor: "#D4A855" }}
                                        data-testid="gutschein-popup-datenschutz"
                                    />
                                    <span>
                                        <PrivacyText
                                            text={cfg.form_privacy_text}
                                            linkText={cfg.form_privacy_link_text}
                                            linkUrl={cfg.form_privacy_link_url}
                                        /> *
                                    </span>
                                </label>
                                {errs.datenschutz && (
                                    <p className="text-xs -mt-2" style={{ color: "#FCA5A5" }}>{errs.datenschutz}</p>
                                )}

                                {status === "error" && (
                                    <div
                                        className="p-2.5 rounded text-xs"
                                        style={{
                                            background: "rgba(239, 68, 68, 0.12)",
                                            color: "#FCA5A5",
                                            border: "1px solid rgba(239, 68, 68, 0.35)",
                                        }}
                                        data-testid="gutschein-popup-error"
                                    >
                                        {errorMsg}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={status === "sending" || soldOut}
                                    className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-md uppercase text-base transition"
                                    style={{
                                        background: soldOut
                                            ? "#475569"
                                            : "linear-gradient(180deg, #F1D48A 0%, #D4A855 100%)",
                                        color: "#0F1830",
                                        border: "1.5px solid #8A6A2C",
                                        boxShadow: soldOut ? "none" : "0 8px 24px -8px rgba(212,168,85,0.55)",
                                        fontFamily: "'Inter', 'Manrope', system-ui, sans-serif",
                                        fontWeight: 800,
                                        letterSpacing: "0.05em",
                                        opacity: status === "sending" ? 0.7 : 1,
                                    }}
                                    data-testid="gutschein-popup-submit"
                                >
                                    {status === "sending" ? "Wird gesendet…" : soldOut ? "Aktion beendet" : cfg.cta_text}
                                    {status !== "sending" && !soldOut && <ChevronRight size={20} strokeWidth={3} />}
                                </button>
                            </form>
                        ) : (
                            <button
                                type="button"
                                onClick={scrollToFallbackAndClose}
                                disabled={soldOut}
                                className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-md uppercase text-base mt-3 transition"
                                style={{
                                    background: soldOut
                                        ? "#475569"
                                        : "linear-gradient(180deg, #F1D48A 0%, #D4A855 100%)",
                                    color: "#0F1830",
                                    border: "1.5px solid #8A6A2C",
                                    boxShadow: soldOut ? "none" : "0 8px 24px -8px rgba(212,168,85,0.55)",
                                    fontFamily: "'Inter', 'Manrope', system-ui, sans-serif",
                                    fontWeight: 800,
                                    letterSpacing: "0.05em",
                                }}
                                data-testid="gutschein-popup-cta"
                            >
                                {soldOut ? "Aktion beendet" : cfg.cta_text}
                                {!soldOut && <ChevronRight size={20} strokeWidth={3} />}
                            </button>
                        )}

                        {/* Reassurance-Zeile */}
                        {cfg.reassurance_line ? (
                            <p
                                className="text-xs md:text-sm mt-0"
                                style={{ color: "#94A3B8" }}
                                data-testid="gutschein-popup-reassurance"
                            >
                                {cfg.reassurance_line}
                            </p>
                        ) : null}

                        {/* Dismiss-Link */}
                        <button
                            type="button"
                            onClick={handleClose}
                            className="text-xs md:text-sm underline mt-1"
                            style={{ color: "#D4A855", background: "transparent" }}
                            data-testid="gutschein-popup-dismiss"
                        >
                            {cfg.dismiss_text || "Später erinnern"}
                        </button>

                        {/* Footer */}
                        {cfg.footer_text ? (
                            <p
                                className="text-[10px] md:text-xs uppercase tracking-[0.2em] mt-2"
                                style={{ color: "#64748B" }}
                                data-testid="gutschein-popup-footer"
                            >
                                {cfg.footer_text}
                            </p>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Referenz-Layout (Gold-Zertifikats-Siegel, ohne Countdown, ohne „€").
 *  Wird gerendert, wenn `mode === "referenz"` und `referenzCfg` gesetzt ist.
 *  Nutzt denselben Dark-Modal-Wrapper wie das Rabatt-Layout, aber komplett
 *  eigenen Inhalt: Gold-Eyebrow, riesen Anton-Titel, rundes Zertifikats-
 *  Siegel („100 %" / „KOSTENLOS"), italic-Serif-Tagline, Trust-Chips,
 *  Formular und Footer-Zeile. */
function ReferenzBody({
    rcfg, errs, errorMsg, status, datenschutz, setDatenschutz, onSubmit,
}: {
    rcfg: ReferenzPopupConfig;
    errs: { name?: string; plz?: string; phone?: string; datenschutz?: string };
    errorMsg: string;
    status: Status;
    datenschutz: boolean;
    setDatenschutz: (v: boolean) => void;
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}) {
    const inputCls =
        "w-full px-3 py-2.5 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-[#D4A855] transition";
    const inputStyle: React.CSSProperties = {
        background: "#0B1220",
        color: "#F8FAFC",
        border: "1.5px solid #33415C",
    };
    const badges = rcfg.trust_badges || [];
    return (
        <div
            className="px-6 md:px-8 py-8 md:py-10 flex flex-col items-center text-center gap-4"
            data-testid="gutschein-popup-referenz-body"
        >
            {/* Gold-Eyebrow */}
            {rcfg.kicker ? (
                <div
                    className="text-[10px] md:text-xs uppercase tracking-[0.32em]"
                    style={{ color: "#D4A855", fontWeight: 600 }}
                    data-testid="referenz-popup-kicker"
                >
                    {rcfg.kicker}
                </div>
            ) : null}

            {/* Riesen-Titel */}
            <h2
                className="font-anton uppercase leading-[1.05]"
                style={{ color: "#F8FAFC", fontSize: "1.9rem", letterSpacing: "0.005em" }}
                data-testid="referenz-popup-headline"
            >
                {rcfg.headline}
            </h2>

            {/* Gold-Zertifikats-Siegel */}
            <CertificateSeal top={rcfg.seal_top} bottom={rcfg.seal_bottom} />

            {/* Italic Serif-Tagline */}
            {rcfg.tagline ? (
                <p
                    className="font-display text-xl md:text-2xl leading-snug -mt-1"
                    style={{ color: "#F8FAFC", fontStyle: "italic" }}
                    data-testid="referenz-popup-tagline"
                >
                    {rcfg.tagline}
                </p>
            ) : null}

            {/* Trust-Chips */}
            {badges.length > 0 ? (
                <div
                    className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full mt-2"
                    data-testid="referenz-popup-trust"
                >
                    {badges.slice(0, 4).map((label, i) => (
                        <div
                            key={i}
                            className="flex items-center gap-2 px-2.5 py-2 rounded-md text-[10.5px] sm:text-[11px] uppercase"
                            style={{
                                background: "rgba(212, 168, 85, 0.14)",
                                border: "1px solid #D4A855",
                                color: "#F8FAFC",
                                fontWeight: 600,
                                letterSpacing: "0.03em",
                            }}
                            data-testid={`referenz-popup-trust-${i}`}
                        >
                            {i === 0 ? (
                                <ShieldCheck size={16} strokeWidth={2.2} style={{ color: "#D4A855", flexShrink: 0 }} />
                            ) : (
                                <Lock size={16} strokeWidth={2.2} style={{ color: "#D4A855", flexShrink: 0 }} />
                            )}
                            <span className="leading-tight text-left">{label}</span>
                        </div>
                    ))}
                </div>
            ) : null}

            {/* Formular */}
            <form
                onSubmit={onSubmit}
                className="w-full mt-3 grid gap-3"
                data-testid="referenz-popup-form"
                noValidate
            >
                {/* Honeypot */}
                <label
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}
                >
                    Website
                    <input type="text" name="website" tabIndex={-1} autoComplete="off" />
                </label>

                {rcfg.form_headline ? (
                    <p
                        className="font-anton uppercase text-lg tracking-wider text-center"
                        style={{ color: "#F8FAFC" }}
                        data-testid="referenz-popup-form-headline"
                    >
                        {rcfg.form_headline}
                    </p>
                ) : null}

                <div className="text-left">
                    <label className="block text-xs mb-1 font-medium" style={{ color: "#D4A855" }}>
                        {rcfg.form_name_label || "Name"} *
                    </label>
                    <input
                        name="gname" type="text" required autoComplete="name"
                        placeholder={rcfg.form_name_placeholder || undefined}
                        className={inputCls}
                        style={{ ...inputStyle, borderColor: errs.name ? "#EF4444" : "#33415C" }}
                        data-testid="referenz-popup-name"
                    />
                    {errs.name && <p className="mt-1 text-xs" style={{ color: "#FCA5A5" }}>{errs.name}</p>}
                </div>

                <div className="grid grid-cols-2 gap-3 text-left">
                    <div>
                        <label className="block text-xs mb-1 font-medium" style={{ color: "#D4A855" }}>
                            {rcfg.form_phone_label || "Telefon"} *
                        </label>
                        <input
                            name="gphone" type="tel" required autoComplete="tel"
                            placeholder={rcfg.form_phone_placeholder || undefined}
                            className={inputCls}
                            style={{ ...inputStyle, borderColor: errs.phone ? "#EF4444" : "#33415C" }}
                            data-testid="referenz-popup-phone"
                        />
                        {errs.phone && <p className="mt-1 text-xs" style={{ color: "#FCA5A5" }}>{errs.phone}</p>}
                    </div>
                    <div>
                        <label className="block text-xs mb-1 font-medium" style={{ color: "#D4A855" }}>
                            {rcfg.form_plz_label || "PLZ"} *
                        </label>
                        <input
                            name="gplz" type="text" inputMode="numeric"
                            pattern="[0-9]{5}" maxLength={5} required
                            autoComplete="postal-code"
                            placeholder={rcfg.form_plz_placeholder || undefined}
                            className={inputCls}
                            style={{ ...inputStyle, borderColor: errs.plz ? "#EF4444" : "#33415C" }}
                            data-testid="referenz-popup-plz"
                        />
                        {errs.plz && <p className="mt-1 text-xs" style={{ color: "#FCA5A5" }}>{errs.plz}</p>}
                    </div>
                </div>

                <label
                    className="flex items-start gap-2 text-xs md:text-[13px] text-left"
                    style={{ color: "#CBD5E1" }}
                >
                    <input
                        type="checkbox"
                        checked={datenschutz}
                        onChange={(e) => setDatenschutz(e.target.checked)}
                        required
                        className="mt-0.5 h-4 w-4"
                        style={{ accentColor: "#D4A855" }}
                        data-testid="referenz-popup-datenschutz"
                    />
                    <span>
                        <PrivacyText
                            text={rcfg.form_privacy_text}
                            linkText={rcfg.form_privacy_link_text}
                            linkUrl={rcfg.form_privacy_link_url}
                        /> *
                    </span>
                </label>
                {errs.datenschutz && (
                    <p className="text-xs -mt-2" style={{ color: "#FCA5A5" }}>{errs.datenschutz}</p>
                )}

                {status === "error" && (
                    <div
                        className="p-2.5 rounded text-xs"
                        style={{
                            background: "rgba(239, 68, 68, 0.12)",
                            color: "#FCA5A5",
                            border: "1px solid rgba(239, 68, 68, 0.35)",
                        }}
                        data-testid="referenz-popup-error"
                    >
                        {errorMsg}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={status === "sending"}
                    className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-md uppercase text-base transition"
                    style={{
                        background: "linear-gradient(180deg, #F1D48A 0%, #D4A855 100%)",
                        color: "#0F1830",
                        border: "1.5px solid #8A6A2C",
                        boxShadow: "0 8px 24px -8px rgba(212,168,85,0.55)",
                        fontFamily: "'Inter', 'Manrope', system-ui, sans-serif",
                        fontWeight: 800,
                        letterSpacing: "0.05em",
                        opacity: status === "sending" ? 0.7 : 1,
                    }}
                    data-testid="referenz-popup-submit"
                >
                    {status === "sending" ? "Wird gesendet…" : rcfg.cta_text}
                    {status !== "sending" && <ChevronRight size={20} strokeWidth={3} />}
                </button>
            </form>

            {/* Footer */}
            {rcfg.footer_text ? (
                <p
                    className="text-xs md:text-sm mt-1"
                    style={{ color: "#94A3B8" }}
                    data-testid="referenz-popup-footer"
                >
                    {rcfg.footer_text}
                </p>
            ) : null}
        </div>
    );
}

/** Rundes Gold-Zertifikats-Siegel. Zwei konzentrische Kreise mit
 *  Sternchen-Ring und zentralem Text („100 %" oben, „KOSTENLOS" unten).
 *  Inline-SVG, kein externes Asset. */
function CertificateSeal({ top, bottom }: { top: string; bottom: string }) {
    const cx = 100;
    const cy = 100;
    return (
        <div
            className="relative"
            style={{ width: 180, height: 180 }}
            data-testid="referenz-popup-seal"
            aria-hidden
        >
            <svg viewBox="0 0 200 200" width="180" height="180">
                <defs>
                    <radialGradient id="hf-seal-fill" cx="45%" cy="35%" r="70%">
                        <stop offset="0%" stopColor="#F5D68E" />
                        <stop offset="55%" stopColor="#D4A855" />
                        <stop offset="100%" stopColor="#8A6A2C" />
                    </radialGradient>
                    <filter id="hf-seal-shadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="4" />
                    </filter>
                </defs>
                {/* Schatten */}
                <circle cx={cx + 4} cy={cy + 6} r={90} fill="rgba(0,0,0,0.45)" filter="url(#hf-seal-shadow)" />
                {/* Haupt-Siegel */}
                <circle cx={cx} cy={cy} r={90} fill="url(#hf-seal-fill)" stroke="#F8FAFC" strokeWidth={1.5} />
                {/* Innerer Ring */}
                <circle cx={cx} cy={cy} r={72} fill="none" stroke="#FFFDF9" strokeWidth={1.5} opacity={0.85} />
                {/* Sternchen-Ring zwischen den Kreisen */}
                {Array.from({ length: 16 }).map((_, i) => {
                    const a = (Math.PI * 2 * i) / 16 - Math.PI / 2;
                    const r = 81;
                    const x = cx + Math.cos(a) * r;
                    const y = cy + Math.sin(a) * r;
                    return (
                        <circle key={i} cx={x} cy={y} r={1.6} fill="#FFFDF9" opacity={0.9} />
                    );
                })}
                {/* Trennstrich zwischen Top- und Bottom-Text */}
                <line x1={cx - 42} y1={cy + 2} x2={cx + 42} y2={cy + 2} stroke="#FFFDF9" strokeWidth={1} opacity={0.7} />
            </svg>
            <div
                className="absolute inset-0 flex flex-col items-center justify-center"
                style={{ transform: "translateY(-1px)" }}
            >
                <span
                    className="font-anton leading-none"
                    style={{
                        color: "#FFFFFF",
                        fontSize: "2.6rem",
                        letterSpacing: "-0.01em",
                        textShadow: "0 2px 6px rgba(0,0,0,0.35)",
                        lineHeight: 1,
                        marginBottom: 6,
                    }}
                    data-testid="referenz-popup-seal-top"
                >
                    {top}
                </span>
                <span
                    className="font-anton uppercase leading-none"
                    style={{
                        color: "#FFFFFF",
                        fontSize: "0.95rem",
                        letterSpacing: "0.18em",
                        marginTop: 6,
                        textShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    }}
                    data-testid="referenz-popup-seal-bottom"
                >
                    {bottom}
                </span>
            </div>
        </div>
    );
}

/** Roter Zackenkreis mit Prozent-Zahl mittig — bzw. „0 €" im Referenz-
 *  Modus (dann ohne Prozent-Zeichen, mit gleichem Stern-Style). Inline-
 *  SVG, kein externes Asset. */
function DiscountStar({ percent, label, mode = "rabatt" }: { percent: number; label: string; mode?: GutscheinMode }) {
    // 24 Zacken, alternierend zwischen zwei Radien. In SVG-Viewbox 0..200.
    const cx = 100;
    const cy = 100;
    const rOuter = 96;
    const rInner = 78;
    const spikes = 24;
    const points: string[] = [];
    for (let i = 0; i < spikes * 2; i++) {
        const r = i % 2 === 0 ? rOuter : rInner;
        const a = (Math.PI * i) / spikes - Math.PI / 2;
        points.push(`${cx + Math.cos(a) * r},${cy + Math.sin(a) * r}`);
    }
    return (
        <div
            className="relative"
            style={{ width: 180, height: 180 }}
            data-testid="gutschein-popup-star"
            aria-hidden
        >
            <svg viewBox="0 0 200 200" width="180" height="180">
                <defs>
                    <radialGradient id={`hf-star-${mode}`} cx="45%" cy="35%" r="70%">
                        {mode === "referenz" ? (
                            <>
                                <stop offset="0%" stopColor="#F5D68E" />
                                <stop offset="55%" stopColor="#D4A855" />
                                <stop offset="100%" stopColor="#8A6A2C" />
                            </>
                        ) : (
                            <>
                                <stop offset="0%" stopColor="#FB6B4A" />
                                <stop offset="55%" stopColor="#E23A24" />
                                <stop offset="100%" stopColor="#9F1D0D" />
                            </>
                        )}
                    </radialGradient>
                    <filter id="hf-star-shadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="4" />
                    </filter>
                </defs>
                <polygon points={points.join(" ")} fill="rgba(0,0,0,0.5)" filter="url(#hf-star-shadow)" transform="translate(4 6)" />
                <polygon
                    points={points.join(" ")}
                    fill={`url(#hf-star-${mode})`}
                    stroke="#F8FAFC"
                    strokeWidth={1.5}
                />
            </svg>
            <div
                className="absolute inset-0 flex flex-col items-center justify-center"
                style={{ transform: "translateY(-2px)" }}
            >
                <span
                    className="font-anton leading-none"
                    style={{
                        color: "#FFFFFF",
                        fontSize: mode === "referenz" ? "2.6rem" : "3.4rem",
                        letterSpacing: "-0.02em",
                        textShadow: "0 2px 6px rgba(0,0,0,0.35)",
                        lineHeight: 1,
                    }}
                >
                    {mode === "referenz" ? "0 €" : `${percent}%`}
                </span>
                {label ? (
                    <span
                        className="font-display italic tracking-wide"
                        style={{
                            color: "#FFFFFF",
                            fontSize: "1.1rem",
                            marginTop: 2,
                            textShadow: "0 1px 3px rgba(0,0,0,0.3)",
                        }}
                    >
                        {label}
                    </span>
                ) : null}
            </div>
        </div>
    );
}

/** Erfolgs-Ansicht nach erfolgreichem Formular-Submit. */
function SuccessState({ headline, body, onClose }: { headline: string; body: string; onClose: () => void }) {
    return (
        <div
            className="px-6 md:px-8 py-10 md:py-12 flex flex-col items-center text-center gap-5"
            data-testid="gutschein-popup-success"
        >
            <div
                className="inline-flex items-center justify-center w-16 h-16 rounded-full"
                style={{
                    background: "linear-gradient(180deg, #F1D48A 0%, #D4A855 100%)",
                    boxShadow: "0 8px 24px -6px rgba(212,168,85,0.5)",
                }}
            >
                <Check size={36} strokeWidth={3.2} style={{ color: "#0F1830" }} />
            </div>
            <h2
                className="font-anton uppercase leading-tight"
                style={{ color: "#F8FAFC", fontSize: "1.8rem", letterSpacing: "0.01em" }}
                data-testid="gutschein-popup-success-headline"
            >
                {headline}
            </h2>
            <p
                className="text-base md:text-lg leading-relaxed max-w-md"
                style={{ color: "#CBD5E1" }}
                data-testid="gutschein-popup-success-body"
            >
                {body}
            </p>
            <div
                className="flex items-center gap-2 text-xs uppercase tracking-wider mt-1"
                style={{ color: "#D4A855" }}
            >
                <ShieldCheck size={16} strokeWidth={2.2} />
                <span>Unverbindlich · Kostenlos · Keine Verpflichtung</span>
            </div>
            <button
                type="button"
                onClick={onClose}
                className="mt-3 px-6 py-2.5 rounded-md uppercase text-sm"
                style={{
                    background: "transparent",
                    color: "#D4A855",
                    border: "1.5px solid #D4A855",
                    fontFamily: "'Inter', 'Manrope', system-ui, sans-serif",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                }}
                data-testid="gutschein-popup-success-close"
            >
                Popup schließen
            </button>
        </div>
    );
}
